/* Tracks which comment threads and scoping questions you've already seen —
   Slack's unread dot, kept on the server per person so it follows you across
   devices (`useStudioMember`). Thread and question ids are unique across
   every policy, so one map per kind is enough. Shared per kind so every place
   a thread or question renders (the row-detail slideover, the main Scoping
   page, the Design rail) agrees on what's unread at once. */
import { createSharedComposable, whenever } from '@vueuse/core'
import { isActivityUnread } from '~/utils/policy-studio/conversation'
import { policyRegistry } from '~/utils/policy-studio/registry'
import { dropLegacy, takeLegacy, useStudioMember } from '~/composables/useStudioTeams'

export interface PolicySeenTracker {
  isUnread: (id: string, activityAt: number | string | undefined) => boolean
  markSeen: (id: string, activityAt: number | string | undefined) => void
}

type SeenKind = 'thread' | 'question'

/* Browser keys already moved to the server this session. */
const migrated = new Set<string>()

const at = (value: number | string | undefined): number | undefined => {
  const time = typeof value === 'string' ? Date.parse(value) : value
  return Number.isFinite(time) ? time : undefined
}

const makeSeenTracker = (kind: SeenKind): PolicySeenTracker => {
  const { user } = useUserSession()
  const { member, report } = useStudioMember()
  const seen = computed(() => member.value?.seen[kind] ?? {})

  const send = (marks: Record<string, number>) =>
    policyRegistry.markSeen(kind, marks).catch(report)

  /* Once: what this browser marked read before read state moved to the server. */
  whenever(
    member,
    () => {
      const key = `admin:policy-studio:seen:${kind}:${user.value?.sub ?? 'anon'}`
      /* Kept until the server has it, so a failed save loses no marks; read
         once, since writing the marks below re-triggers this watcher. */
      if (migrated.has(key)) return
      const legacy = takeLegacy<Record<string, number>>(key, false)
      if (!legacy) return
      migrated.add(key)
      /* ponytail: the newest 1000 marks, the server's batch limit; older ones
         read as unread once more. */
      const newest = Object.entries(legacy)
        .filter(([, time]) => Number.isFinite(time))
        .toSorted(([, a], [, b]) => b - a)
        .slice(0, 1000)
      if (!newest.length) return dropLegacy(key)
      const marks = Object.fromEntries(newest)
      member.value = {
        ...member.value!,
        seen: { ...member.value!.seen, [kind]: { ...marks, ...seen.value } },
      }
      void policyRegistry
        .markSeen(kind, marks)
        .then(() => dropLegacy(key), report)
    },
    { immediate: true, once: true },
  )

  /* Nothing reads as unread until your marks have loaded, so dots never flash. */
  const isUnread: PolicySeenTracker['isUnread'] = (id, activityAt) =>
    !!member.value && isActivityUnread(seen.value[id], activityAt)

  const markSeen: PolicySeenTracker['markSeen'] = (id, activityAt) => {
    const activity = at(activityAt) ?? Date.now()
    if ((seen.value[id] ?? 0) >= activity) return
    /* Before your marks load, the server still keeps it: it keeps the later. */
    if (member.value)
      member.value = {
        ...member.value,
        seen: { ...member.value.seen, [kind]: { ...seen.value, [id]: activity } },
      }
    void send({ [id]: activity })
  }

  return { isUnread, markSeen }
}

const useThreadSeen = createSharedComposable(() => makeSeenTracker('thread'))
const useQuestionSeen = createSharedComposable(() =>
  makeSeenTracker('question'),
)

export const usePolicySeen = (kind: SeenKind): PolicySeenTracker =>
  kind === 'thread' ? useThreadSeen() : useQuestionSeen()
